import { useState, useEffect } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Timer, Clock, CheckCircle, AlertTriangle } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import {
  PageContainer, PageCard, PageHeader, PageHeaderContent,
  PageHeaderTitle, PageHeaderHeading, PageContent
} from '@/components/ui/page'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { cn } from '@/lib/utils'
import { useAdminOrgBillingBalance } from '@/hooks/admin/useAdminOrgBillingBalance'
import { useAdminOrgBillingHistory } from '@/hooks/admin/useAdminOrgBillingHistory'
import { type AdminBillingTimeRange } from '@/lib/billing/formatters'
import { useAdminBillingCycles } from '@/hooks/admin/useAdminBillingCycles'
import { useAdminOrganization } from '@/hooks/admin/useAdminOrganization'
import { OrgUsageTable } from '@/components/admin/billing/OrgUsageTable'
import { OrgTopUpsTable } from '@/components/admin/billing/OrgTopUpsTable'

export const Route = createFileRoute('/admin/billing/$orgId')({
  component: BillingOrgDetail,
  errorComponent: RouteErrorFallback,
})

const chartConfig = {
  call_count: {
    label: 'Anrufe',
    color: 'var(--chart-1)',
  },
  used_minutes: {
    label: 'Minuten',
    color: 'var(--chart-2)',
  },
} satisfies ChartConfig

function formatCycleLabel(cycleStart: string, cycleEnd: string): string {
  const fmt = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }
  return `${fmt(cycleStart)} - ${fmt(cycleEnd)}`
}

function BillingOrgDetail() {
  const { orgId } = Route.useParams()
  const { data: organization } = useAdminOrganization(orgId)

  const { data: cyclesData } = useAdminBillingCycles(orgId)
  const [selectedCycleStart, setSelectedCycleStart] = useState<string | null>(null)

  // Default to current cycle once cycles load
  useEffect(() => {
    if (cyclesData?.cycles && selectedCycleStart === null) {
      const current = cyclesData.cycles.find(c => c.is_current)
      if (current) setSelectedCycleStart(current.cycle_start)
    }
  }, [cyclesData, selectedCycleStart])

  const selectedCycle = cyclesData?.cycles.find(c => c.cycle_start === selectedCycleStart)
  const isCurrent = selectedCycle?.is_current ?? true

  // Only pass cycleStart to balance API for non-current cycles
  const balanceCycleStart = isCurrent ? undefined : selectedCycleStart
  const { data: balance, isLoading: balanceLoading } = useAdminOrgBillingBalance(orgId, balanceCycleStart)

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <div className="flex items-center gap-2">
                <Link to="/admin/billing" className="text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="h-5 w-5" />
                </Link>
                <PageHeaderHeading>{organization?.name ?? orgId}</PageHeaderHeading>
              </div>
            </PageHeaderTitle>
            {cyclesData && cyclesData.cycles.length > 0 && (
              <Select
                value={selectedCycleStart ?? undefined}
                onValueChange={setSelectedCycleStart}
              >
                <SelectTrigger className="w-full sm:w-[260px]">
                  <SelectValue placeholder="Abrechnungszeitraum" />
                </SelectTrigger>
                <SelectContent>
                  {cyclesData.cycles.map((cycle) => (
                    <SelectItem key={cycle.cycle_start} value={cycle.cycle_start}>
                      {formatCycleLabel(cycle.cycle_start, cycle.cycle_end)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </PageHeaderContent>
        </PageHeader>
        <PageContent className="overflow-y-auto">
          <div className="flex flex-col gap-4 p-4 flex-1 min-h-0">
            <BalanceCards balance={balance} isLoading={balanceLoading} />
            <OrgUsageChart
              orgId={orgId}
              cycleStart={selectedCycle?.cycle_start}
              cycleEnd={selectedCycle?.cycle_end}
            />
            <Tabs defaultValue="calls" className="flex-1 min-h-0">
              <TabsList>
                <TabsTrigger value="calls">Anrufdetails</TabsTrigger>
                <TabsTrigger value="topups">Guthaben</TabsTrigger>
              </TabsList>
              <TabsContent value="calls" className="min-h-0 flex flex-col">
                <OrgUsageTable
                  organizationId={orgId}
                  className="flex-1 min-h-0"
                  startTime={selectedCycle?.cycle_start}
                  endTime={selectedCycle?.cycle_end}
                />
              </TabsContent>
              <TabsContent value="topups" className="min-h-0 flex flex-col">
                <OrgTopUpsTable
                  organizationId={orgId}
                  organizationName={organization?.name ?? 'Organisation'}
                  className="flex-1 min-h-0"
                />
              </TabsContent>
            </Tabs>
          </div>
        </PageContent>
      </PageCard>
    </PageContainer>
  )
}

function BalanceCards({ balance, isLoading }: {
  balance: ReturnType<typeof useAdminOrgBillingBalance>['data']
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="py-3 gap-1">
            <CardHeader className="flex flex-row items-center justify-between px-4 pb-0">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent className="px-4">
              <Skeleton className="h-7 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!balance) return null

  const cards = [
    {
      title: 'Kontingent',
      value: `${Math.round(balance.allowance_minutes)} Min.`,
      icon: Timer,
      destructive: false,
    },
    {
      title: 'Verbraucht',
      value: `${Math.round(balance.used_minutes_current_cycle)} Min.`,
      icon: Clock,
      destructive: false,
    },
    {
      title: 'Verfügbar',
      value: `${Math.round(balance.total_available)} Min.`,
      icon: CheckCircle,
      destructive: false,
    },
    {
      title: 'Status',
      value: balance.is_exceeded ? 'Überschritten' : 'OK',
      icon: balance.is_exceeded ? AlertTriangle : CheckCircle,
      destructive: balance.is_exceeded,
    },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((card) => (
        <Card key={card.title} className="py-3 gap-1">
          <CardHeader className="flex flex-row items-center justify-between px-4 pb-0">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <card.icon className={cn("h-4 w-4", card.destructive ? 'text-destructive' : 'text-muted-foreground')} />
          </CardHeader>
          <CardContent className="px-4">
            <div className={cn("text-2xl font-bold", card.destructive && 'text-destructive')}>
              {card.value}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function OrgUsageChart({ orgId, cycleStart, cycleEnd }: {
  orgId: string
  cycleStart?: string
  cycleEnd?: string
}) {
  const [timeRange, setTimeRange] = useState<AdminBillingTimeRange>('woche')
  const { buckets, isLoading } = useAdminOrgBillingHistory(orgId, timeRange, cycleStart, cycleEnd)

  return (
    <Card className="py-2 gap-1">
      <CardHeader className="px-4 pt-0 pb-0 flex-shrink-0">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-sm">Nutzung</CardTitle>
            <CardDescription>
              {timeRange === 'woche' ? 'Letzte 7 Tage' : 'Letzte 30 Tage'}
            </CardDescription>
          </div>
          <Tabs value={timeRange} onValueChange={(v) => setTimeRange(v as AdminBillingTimeRange)}>
            <TabsList>
              <TabsTrigger value="woche">Woche</TabsTrigger>
              <TabsTrigger value="monat">Monat</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-0 flex-1 min-h-0">
        {isLoading ? (
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : buckets.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-muted-foreground">
            Keine Daten verfügbar
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-48 w-full">
            <AreaChart data={buckets} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="fillOrgCalls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-call_count)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-call_count)" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="fillOrgMinutes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-used_minutes)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-used_minutes)" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                fontSize={10}
                interval="preserveStartEnd"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={4}
                fontSize={10}
                allowDecimals={false}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => value}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Area
                dataKey="call_count"
                type="monotone"
                fill="url(#fillOrgCalls)"
                stroke="var(--color-call_count)"
                strokeWidth={1.5}
              />
              <Area
                dataKey="used_minutes"
                type="monotone"
                fill="url(#fillOrgMinutes)"
                stroke="var(--color-used_minutes)"
                strokeWidth={1.5}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
